import axios from "axios";
import { Capacitor } from "@capacitor/core";
import * as Common from "../utiles/common.util";
import * as store from "../stores/store";
import { Preferences } from "@capacitor/preferences";

const version = "/api/v1/";

const isNative = Capacitor.isNativePlatform(); // 앱이면 true
const isBrowser = !isNative; // 브라우저면 true

// 에뮬레이터는 Android Studio 기본값이면 hostname 체크
// 그냥 Android 에뮬레이터는 Capacitor.isNativePlatform() && !deviceId 체크로도 가능
const isEmulator =
    (isNative && /10\.0\.2\.2/.test(window.location.href)) || false;

// 기본 baseURL
let baseURL = "";

// 1. 모바일 앱 (실제 디바이스)
if (isNative && !isEmulator) {
    // 실제 모바일에서 로컬 서버 접속
    // baseURL = "http://192.168.0.42" + version;
    baseURL = "https://surittok.com" + version;
}

// 2. 모바일 에뮬레이터
else if (isNative && isEmulator) {
    baseURL = "http://10.0.2.2" + version; // 에뮬레이터 localhost
}

// 3. 웹 브라우저
else if (isBrowser) {
    const host = window.location.hostname;
    baseURL =
        host === "localhost" || host === "192.168.0.42"
            ? "http://" + host + version // 개발용
            : "https://surittok.com" + version; // 운영용
}

const instance = axios.create({
    baseURL,
    timeout: 54000,
    withCredentials: true,
});

// ---------------------------------------------------------------------
// [토큰 갱신 관련 변수]
let isRefreshing = false;
let subscribers = [];

function onRefreshed(newToken) {
    subscribers.forEach((callback) => callback(newToken));
    subscribers = [];
}

function addSubscriber(callback) {
    subscribers.push(callback);
}
// ---------------------------------------------------------------------

// 요청 인터셉터
instance.interceptors.request.use(
    async (config) => {
        const token = await Common.getStoredToken(); // 로컬 스토리지 또는 Preferences에서 가져옴
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

// 응답 인터셉터 (401 에러 감지 시 Refresh 로직 실행)
instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;
        const originalRequest = config;

        // 401 에러(만료)가 났고, 재시도한 적이 없는 요청일 때
        if (response && response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // 이미 다른 요청이 갱신 중이라면 큐에서 대기
            if (isRefreshing) {
                return new Promise((resolve) => {
                    addSubscriber((newToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(instance(originalRequest));
                    });
                });
            }

            isRefreshing = true;

            try {
                // 리프레시 토큰 가져오기 (Common 유틸에 getStoredRefreshToken 함수가 있다고 가정)
                const refreshToken = await Common.getStoredRefreshToken();

                if (!refreshToken) {
                    throw new Error("No refresh token");
                }

                // 서버에 토큰 갱신 요청 (FormData 형식 유지)
                const formData = new FormData();
                formData.append("login_m_token", refreshToken);

                const refreshRes = await axios.post(
                    baseURL + "account/refresh",
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    },
                );

                if (refreshRes.data.code === 200) {
                    const newAccessToken = refreshRes.data.token.accessToken;
                    const newRefreshToken = refreshRes.data.token.refreshToken;

                    store.jwt_token.set(newAccessToken);
                    store.refreshToken.set(newRefreshToken);

                    // 새로운 토큰들 저장
                    await Preferences.set({
                        key: "jwt_token",
                        value: newAccessToken,
                    });
                    if (newRefreshToken) {
                        await Preferences.set({
                            key: "refreshToken",
                            value: newRefreshToken,
                        });
                    }

                    isRefreshing = false;
                    onRefreshed(newAccessToken);

                    // 원래 실패했던 요청 재시도
                    originalRequest.headers = {
                        ...originalRequest.headers,
                        Authorization: `Bearer ${newAccessToken}`,
                    };

                    return instance(originalRequest);
                }
            } catch (refreshError) {
                isRefreshing = false;
                // 리프레시 실패 시 로그아웃 처리 및 이동
                store.accountInfo.set(null);
                store.jwt_token.set(null);
                store.refreshToken.set(null);
                await Preferences.clear();

                // 로그인 페이지로 이동
                push("/Login");

                window.location.href = "/login.html";
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    },
);

export default instance;
